import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, ROLE_LABELS, postAuthDestination } from "@/contexts/AuthContext";
import { Hourglass, LogOut, Mail, RefreshCw, CheckCircle2 } from "lucide-react";

export default function AccountPendingVerification() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);

  // If user gets verified (e.g. admin approves while this page is open), bounce to dashboard
  useEffect(() => {
    if (user && user.verified === true) {
      navigate(postAuthDestination(user), { replace: true });
    }
  }, [user, navigate]);

  // Auto-poll every 30 seconds to silently re-check verification status
  useEffect(() => {
    const id = setInterval(() => { checkAuth?.(); }, 30000);
    return () => clearInterval(id);
  }, [checkAuth]);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await checkAuth?.();
    } finally {
      setRefreshing(false);
    }
  };

  const onLogout = async () => {
    await logout?.();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-kb-bg flex items-center justify-center px-6" data-testid="pending-verification-page">
      <div className="w-full max-w-lg text-center kb-card p-10">
        <div className="mx-auto w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
          <Hourglass className="w-7 h-7 text-amber-600" strokeWidth={1.5} />
        </div>
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mt-5">PENDING ADMIN APPROVAL</div>
        <h1 className="font-serif text-3xl text-kb-ink mt-2 leading-tight">
          Welcome, <em className="italic text-kb-primary">{user?.name?.split(" ")[0]}</em>.
        </h1>
        <p className="text-kb-ink-muted mt-3">
          Your <strong className="text-kb-ink">{ROLE_LABELS[user?.role] || user?.role}</strong> account is being reviewed by KhojByte admins.
          Once approved, you'll get access to the full workspace. This typically takes 24-48 hours.
        </p>
        <div className="mt-6 bg-slate-50 border border-slate-200 rounded-sm p-4 text-left text-sm space-y-2">
          <div className="flex items-center gap-2 text-kb-ink-muted">
            <Mail className="w-3.5 h-3.5" />
            <span>Account email: <span className="font-mono text-kb-ink">{user?.email}</span></span>
          </div>
          <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted flex items-center gap-1.5">
            Status:
            {user?.verification_status === "rejected" ? (
              <span className="text-rose-600">rejected — please contact admin</span>
            ) : (
              <span className="text-amber-700">awaiting verification</span>
            )}
            <span className="ml-auto text-kb-ink-light flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3 text-emerald-500" /> auto-checking every 30s
            </span>
          </div>
        </div>
        <div className="mt-6 flex flex-col items-center gap-3">
          <button onClick={onRefresh} disabled={refreshing} className="kb-btn-primary text-sm flex items-center gap-2" data-testid="pending-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Checking…" : "Check status now"}
          </button>
          <button onClick={onLogout} className="kb-mono text-xs text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-1.5" data-testid="pending-logout-btn">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light mt-6">
          Need help? <Link to="/contact" className="text-kb-primary">Contact KhojByte</Link>
        </div>
      </div>
    </div>
  );
}
